import { useEffect, useRef, useState } from "react";
import { isConnectionValidationAccount } from "@/config/connectionValidation";
import { useLibraryIdentity } from "@/hooks/useLibraryIdentity";
import { visiblePeopleTaskProviders } from "@/lib/peopleTasks";
import { supabase } from "@/integrations/supabase/client";
import { createMirrorAuthSessionScope } from "@/lib/mirrorAuthSession";

/** Rollout remains separate from native review cancellation identity. */
export function usePeopleTaskConnectionAccess() {
  const identity = useLibraryIdentity();
  const scope = useRef(createMirrorAuthSessionScope()).current;
  const [epoch,setEpoch] = useState(0);
  const providers = visiblePeopleTaskProviders(isConnectionValidationAccount(identity));
  const nativeEnabled=providers.includes("apple_reminders");
  useEffect(()=>{
    if(!nativeEnabled)return;
    let mounted=true;
    const observe=(session:unknown)=>{
      if(!mounted)return;
      const next=scope.observeSession(session);
      if(next.changed)setEpoch(value=>value+1);
    };
    const {data:{subscription}}=supabase.auth.onAuthStateChange((_event,session)=>observe(session));
    const observed=scope.revision();
    void supabase.auth.getSession().then(reply=>{
      if(mounted&&scope.revision()===observed)observe(reply.error?null:reply.data.session);
    }).catch(()=>{if(mounted&&scope.revision()===observed)observe(null);});
    return()=>{mounted=false;scope.invalidate();subscription.unsubscribe();};
  },[scope,nativeEnabled]);
  const userId = identity.ready && !identity.error ? identity.userId : null;
  const lease=scope.capture();
  return {
    providers,
    googleContactsEnabled: providers.includes("google_contacts"),
    microsoftContactsEnabled: providers.includes("microsoft_contacts"),
    microsoftTodoEnabled: providers.includes("microsoft_todo"),
    googleTasksEnabled: providers.includes("google_tasks"),
    userId,
    identityKey: identity.ready && !identity.error ? identity.userId ?? "signed-out" : "unresolved",
    nativeReviewKey: (identity.ready && !identity.error ? identity.userId ?? "signed-out" : "unresolved")+":"+epoch,
    nativeIsCurrent: ()=>scope.isCurrent(lease)&&lease?.ownerId===userId,
  };
}
